/**
 * MarketplaceListingsSection — "Marketplace Listings" on the Item detail page.
 *
 * iPad Settings UI: single rounded-2xl card containing all channels.
 * Each connected channel is collapsible. Unconnected channels shown for discovery.
 *
 * Plan 53, Epic 03.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ChannelLogo } from "@/features/channels/components/ChannelLogo";
import { LinkItemDialog } from "@/features/channels/components/LinkItemDialog";
import { SyncFreshnessDot } from "@/features/channels/components/SyncFreshnessDot";
import { SyncActivityTimeline } from "@/features/channels/components/SyncActivityTimeline";
import { useItemListings, useUnlinkListing, usePushItemToChannel } from "@/features/channels/api/listingsHooks";
import { useMarketplaceConnections } from "@/features/channels/api/marketplaceHooks";
import { ListOnChannelSheet } from "@/features/channels/components/ListOnChannelSheet";
import { SectionHeader } from "../../../SectionHeader";
import type { MarketplaceListing } from "@/features/channels/api/listingsApi";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { ChevronRight, Link2, Loader2, Check, Plus } from "lucide-react";
import {
  ListingStatusPill as StatusPill,
  formatPrice,
  formatSyncTime,
} from "@/features/channels/components/ListingStatusPill";

/** Maps channel display name → DocType name for navigation */
const CHANNEL_DOCTYPE: Record<string, string> = {
  "TikTok Shop": "TikTok Listing",
  "Lazada": "Lazada Listing",
  "Shopee": "Shopee Listing",
};

/** All channels we support — always shown for feature discovery */
const ALL_CHANNELS = ["TikTok Shop", "Lazada", "Shopee"] as const;

/** Standard iPad Settings divider */
function Divider({ dividerMx = "mx-4" }: { dividerMx?: string }) {
  return <div className={cn(dividerMx, "border-t border-border/40")} />;
}

/** Standard iPad Settings key-value row */
function SettingsRow({ label, value, rowPx = "px-4", rowPy = "py-5", rowGap = "gap-4", rowLabel = "text-lg" }: {
  label: string;
  value: string;
  rowPx?: string;
  rowPy?: string;
  rowGap?: string;
  rowLabel?: string;
}) {
  return (
    <div className={cn(`flex items-center ${rowGap}`, rowPx, rowPy)}>
      <span
        className={cn(rowLabel, "w-1/2")}
        style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
      >
        {label}
      </span>
      <div className={cn(rowLabel, "w-1/2 text-right text-foreground")}>
        <span className="break-words whitespace-pre-wrap">{value || "\u2014"}</span>
      </div>
    </div>
  );
}

// --- Connected Channel Group (collapsible within card) ---

function ConnectedChannelGroup({
  channel,
  listings,
  expanded,
  onToggle,
  onViewListing,
  onUnlink,
  onPush,
  isPushing,
  pushFeedback,
  rowPx = "px-4",
  rowPy = "py-5",
  rowGap = "gap-4",
  rowLabel = "text-lg",
  dividerMx = "mx-4",
}: {
  channel: string;
  listings: MarketplaceListing[];
  expanded: boolean;
  onToggle: () => void;
  onViewListing?: (listingName: string) => void;
  /** Unlink callback — only shown when item has 2+ channels */
  onUnlink?: (listingName: string, listingDoctype: string) => void;
  /** Push now callback (Plan 192) */
  onPush?: (listingName: string) => void;
  isPushing?: boolean;
  pushFeedback?: { success: boolean; error?: string } | null;
  rowPx?: string;
  rowPy?: string;
  rowGap?: string;
  rowLabel?: string;
  dividerMx?: string;
}) {
  const [confirmingUnlink, setConfirmingUnlink] = useState(false);
  const listing = listings[0];
  const sync = formatSyncTime(listing.last_synced);
  const price =
    listing.price != null ? formatPrice(listing.price, listing.currency) : null;
  const doctype = CHANNEL_DOCTYPE[channel];

  return (
    <>
      {/* Channel header — tappable to collapse/expand */}
      <button
        type="button"
        className={cn(`flex items-center ${rowGap} w-full text-left`, rowPx, rowPy)}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3 w-1/2 min-w-0">
          <ChannelLogo channel={channel} size={20} className="shrink-0" />
          <span
            className={cn(rowLabel, "font-medium truncate")}
            style={{ color: "rgb(17 24 39 / var(--tw-text-opacity, 1))" }}
          >
            {channel}
          </span>
        </div>
        <div className="w-1/2 flex items-center justify-end gap-2">
          <SyncFreshnessDot
            lastOutboundSync={listing.last_outbound_sync}
            lastOutboundStatus={listing.last_outbound_status}
          />
          <StatusPill status={listing.status} />
          <ChevronRight
            className={cn(
              "h-4 w-4 text-muted-foreground/60 transition-transform duration-200",
              expanded && "rotate-90",
            )}
          />
        </div>
      </button>

      {/* Collapsible detail rows */}
      <div
        className={cn(
          "grid transition-[grid-template-rows] duration-200",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="overflow-hidden">
          <Divider dividerMx={dividerMx} />
          <SettingsRow label="Product" value={listing.listing_title || "\u2014"} rowPx={rowPx} rowPy={rowPy} rowGap={rowGap} rowLabel={rowLabel} />
          {price && (
            <>
              <Divider dividerMx={dividerMx} />
              <SettingsRow label="Price" value={price} rowPx={rowPx} rowPy={rowPy} rowGap={rowGap} rowLabel={rowLabel} />
            </>
          )}
          <Divider dividerMx={dividerMx} />
          <SettingsRow label="Last Synced" value={sync.relative || "\u2014"} rowPx={rowPx} rowPy={rowPy} rowGap={rowGap} rowLabel={rowLabel} />

          {/* Outbound push details — only shown when data exists (Plan 192) */}
          {listing.last_stock_push_at && (
            <>
              <Divider dividerMx={dividerMx} />
              <SettingsRow
                label="Stock Pushed"
                value={`${listing.last_stock_push_qty ?? 0} units \u00b7 ${formatSyncTime(listing.last_stock_push_at).relative}`}
                rowPx={rowPx} rowPy={rowPy} rowGap={rowGap} rowLabel={rowLabel}
              />
            </>
          )}
          {listing.last_price_push_at && (
            <>
              <Divider dividerMx={dividerMx} />
              <SettingsRow
                label="Price Pushed"
                value={`${formatPrice(listing.last_price_push_value ?? 0, listing.currency)} \u00b7 ${formatSyncTime(listing.last_price_push_at).relative}`}
                rowPx={rowPx} rowPy={rowPy} rowGap={rowGap} rowLabel={rowLabel}
              />
            </>
          )}

          {/* Push now action (Plan 192) */}
          {onPush && (
            <>
              <Divider dividerMx={dividerMx} />
              <button
                type="button"
                className={cn(
                  `flex items-center ${rowGap} w-full text-left hover:bg-muted/50 transition-colors`,
                  rowPx, rowPy,
                )}
                onClick={() => onPush(listing.name)}
                disabled={isPushing}
              >
                <span className={cn(rowLabel, "text-primary flex-1")}>Push now</span>
                {isPushing ? (
                  <Loader2 className="h-4 w-4 animate-spin text-primary shrink-0" />
                ) : pushFeedback?.success ? (
                  <Check className="h-4 w-4 text-green-500 shrink-0" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-primary/60 shrink-0" />
                )}
              </button>
              {pushFeedback && !pushFeedback.success && pushFeedback.error && (
                <div className={cn(rowPx, "pb-3")}>
                  <p className="text-xs text-destructive">{pushFeedback.error}</p>
                </div>
              )}
            </>
          )}

          {listings.length > 1 && (
            <>
              <Divider dividerMx={dividerMx} />
              <div className={cn(rowPx, rowPy, "text-center")}>
                <span className="text-sm text-muted-foreground">
                  +{listings.length - 1} more listing{listings.length > 2 ? "s" : ""}
                </span>
              </div>
            </>
          )}

          {/* View listing — navigates to listing detail page */}
          {onViewListing && (
            <>
              <Divider dividerMx={dividerMx} />
              <button
                type="button"
                className={cn(`flex items-center ${rowGap} w-full text-left hover:bg-muted/50 transition-colors`, rowPx, rowPy)}
                onClick={() => onViewListing(listing.name)}
              >
                <span className={cn(rowLabel, "text-primary flex-1")}>View listing</span>
                <ChevronRight className="h-5 w-5 text-primary/60 shrink-0" />
              </button>
            </>
          )}

          {/* Unlink ��� only shown when item has 2+ channels */}
          {onUnlink && doctype && (
            <>
              <Divider dividerMx={dividerMx} />
              {!confirmingUnlink ? (
                <button
                  type="button"
                  className={cn(
                    `flex items-center ${rowGap} w-full text-left hover:bg-destructive/5 transition-colors`,
                    rowPx, rowPy,
                  )}
                  onClick={() => setConfirmingUnlink(true)}
                >
                  <span className="text-sm text-destructive flex-1">
                    Unlink from this item
                  </span>
                </button>
              ) : (
                <div className={cn(rowPx, rowPy)}>
                  <p className="text-sm text-muted-foreground mb-3">
                    This will create a separate item for this {channel} listing. Continue?
                  </p>
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setConfirmingUnlink(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => {
                        onUnlink(listing.name, doctype);
                        setConfirmingUnlink(false);
                      }}
                    >
                      Unlink
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

// --- Unconnected Channel Row (discovery/marketing) ---

function UnconnectedChannelRow({ channel, rowPx = "px-4", rowPy = "py-5", rowGap = "gap-4", rowLabel = "text-lg", canList, onListOnChannel }: {
  channel: string;
  rowPx?: string;
  rowPy?: string;
  rowGap?: string;
  rowLabel?: string;
  canList?: boolean;
  onListOnChannel?: () => void;
}) {
  if (canList && onListOnChannel) {
    return (
      <button
        type="button"
        className={cn(
          `flex items-center ${rowGap} w-full text-left`,
          "hover:bg-muted/50 transition-colors",
          rowPx, rowPy,
        )}
        onClick={onListOnChannel}
      >
        <div className="flex items-center gap-3 w-1/2 min-w-0">
          <ChannelLogo channel={channel} size={20} className="shrink-0 opacity-50" />
          <span className={cn(rowLabel, "text-muted-foreground truncate")}>{channel}</span>
        </div>
        <div className="w-1/2 flex items-center justify-end gap-1.5">
          <Plus className="h-3.5 w-3.5 text-primary" />
          <span className="text-sm text-primary">List on {channel}</span>
          <ChevronRight className="h-4 w-4 text-primary/50 ml-0.5" />
        </div>
      </button>
    );
  }

  return (
    <div className={cn(`flex items-center ${rowGap}`, rowPx, rowPy)}>
      <div className="flex items-center gap-3 w-1/2 min-w-0">
        <ChannelLogo channel={channel} size={20} className="shrink-0 opacity-50" />
        <span className={cn(rowLabel, "text-muted-foreground truncate")}>{channel}</span>
      </div>
      <div className="w-1/2 flex items-center justify-end">
        <span className="text-sm text-muted-foreground/70">Not connected</span>
      </div>
    </div>
  );
}

// --- Loading skeleton ---

function CardSkeleton() {
  return (
    <div className={CARD}>
      {[0, 1, 2].map((i) => (
        <div key={i}>
          {i > 0 && <Divider />}
          <div className="flex items-center gap-4 px-4 py-5">
            <div className="flex items-center gap-3 w-1/2">
              <Skeleton className="h-5 w-5 rounded-md" />
              <Skeleton className="h-5 w-24 rounded-md" />
            </div>
            <div className="w-1/2 flex justify-end">
              <Skeleton className="h-5 w-20 rounded-md" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Main Widget ---

export function MarketplaceListingsSection({ formData }: ShopifySectionWidgetProps) {
  const navigate = useNavigate();
  const itemCode = formData?.name as string | undefined;
  const { textSize, rowPx, rowPy, rowGap, dividerMx, rowLabel } = useDetailDensity();
  const { data: listings, isLoading, isError, refetch } = useItemListings(itemCode);
  const unlinkMutation = useUnlinkListing();
  const pushMutation = usePushItemToChannel();
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(
    () => new Set(ALL_CHANNELS),
  );
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [listOnChannelState, setListOnChannelState] = useState<{
    channel: string; connectionName: string;
  } | null>(null);
  const { data: connections } = useMarketplaceConnections();
  const [pushingChannel, setPushingChannel] = useState<string | null>(null);
  const [pushFeedback, setPushFeedback] = useState<{
    channel: string; success: boolean; error?: string;
  } | null>(null);

  // Hide for bookable items — marketplace listings not relevant for bookings
  if (formData?.is_bookable === 1 || formData?.is_bookable === true) return null;

  // Item eligibility for "List on Channel" — needs name, price > 0, stock > 0
  const itemName = (formData?.item_name as string) || "";
  const itemPrice = Number(formData?.standard_rate) || 0;
  // Use total stock from form data if available, otherwise 0
  const itemStock = Math.max(0, Math.floor(Number(formData?.total_qty || formData?.actual_qty || 0)));
  const isEligibleToList = !!(itemName && itemPrice > 0 && itemStock > 0);

  /** Find an active connection for a channel that has no listing */
  const getConnectionForChannel = (channel: string): string | null => {
    if (!connections || !isEligibleToList) return null;
    // Map channel display name to connection channel value
    const channelMap: Record<string, string> = {
      "TikTok Shop": "TikTok Shop",
      "Lazada": "Lazada",
      "Shopee": "Shopee",
    };
    const connChannel = channelMap[channel];
    if (!connChannel) return null;
    const conn = connections.find(
      (c) => c.channel === connChannel && c.enabled && c.status === "Active",
    );
    return conn?.name || null;
  };

  const handlePush = (channel: string, listingName: string) => {
    if (!itemCode) return;
    setPushingChannel(channel);
    setPushFeedback(null);
    pushMutation.mutate(
      { itemCode, channel, listingName },
      {
        onSuccess: () => {
          setPushingChannel(null);
          setPushFeedback({ channel, success: true });
          setTimeout(() => setPushFeedback(null), 2000);
        },
        onError: (err: Error) => {
          setPushingChannel(null);
          setPushFeedback({ channel, success: false, error: err.message || "Push failed" });
          setTimeout(() => setPushFeedback(null), 5000);
        },
      },
    );
  };

  const handleViewListing = (channel: string, listingName: string) => {
    const doctype = CHANNEL_DOCTYPE[channel];
    if (!doctype) return;
    navigate(`/doc/${encodeURIComponent(doctype)}/${encodeURIComponent(listingName)}`);
  };

  const toggle = (channel: string) => {
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channel)) next.delete(channel);
      else next.add(channel);
      return next;
    });
  };

  const handleUnlink = (listingName: string, listingDoctype: string) => {
    if (!itemCode) return;
    unlinkMutation.mutate({ listingName, listingDoctype, parentItemCode: itemCode });
  };

  const handleLinked = (targetItemCode: string) => {
    navigate(`/inventory/item/${encodeURIComponent(targetItemCode)}`);
  };

  // Group listings by channel
  const listingsByChannel = new Map<string, MarketplaceListing[]>();
  if (listings) {
    for (const l of listings) {
      const arr = listingsByChannel.get(l.channel) || [];
      arr.push(l);
      listingsByChannel.set(l.channel, arr);
    }
  }

  const connectedChannelCount = listingsByChannel.size;

  return (
    <div>
      <SectionHeader label="Marketplace Listings" textSize={textSize} />
      {connectedChannelCount >= 2 && (
        <p className="text-xs text-muted-foreground pl-1 -mt-1 mb-2">
          {connectedChannelCount} channels connected
        </p>
      )}

      {/* Loading */}
      {isLoading && <CardSkeleton />}

      {/* Error */}
      {isError && (
        <div className={CARD}>
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">Could not load listings</p>
            <button
              type="button"
              className="mt-1 text-sm text-primary hover:underline"
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Single card with all channels */}
      {!isLoading && !isError && (
        <div className={CARD}>
          {ALL_CHANNELS.map((channel, i) => {
            const channelListings = listingsByChannel.get(channel);
            const isConnected = channelListings && channelListings.length > 0;

            return (
              <div key={channel}>
                {i > 0 && <Divider dividerMx={dividerMx} />}
                {isConnected ? (
                  <ConnectedChannelGroup
                    channel={channel}
                    listings={channelListings}
                    expanded={expandedChannels.has(channel)}
                    onToggle={() => toggle(channel)}
                    onViewListing={(name) => handleViewListing(channel, name)}
                    onUnlink={connectedChannelCount >= 2 ? handleUnlink : undefined}
                    onPush={(listingName) => handlePush(channel, listingName)}
                    isPushing={pushingChannel === channel}
                    pushFeedback={pushFeedback?.channel === channel ? pushFeedback : null}
                    rowPx={rowPx}
                    rowPy={rowPy}
                    rowGap={rowGap}
                    rowLabel={rowLabel}
                    dividerMx={dividerMx}
                  />
                ) : (
                  <UnconnectedChannelRow
                    channel={channel}
                    rowPx={rowPx}
                    rowPy={rowPy}
                    rowGap={rowGap}
                    rowLabel={rowLabel}
                    canList={!!getConnectionForChannel(channel)}
                    onListOnChannel={() => {
                      const connName = getConnectionForChannel(channel);
                      if (connName) setListOnChannelState({ channel, connectionName: connName });
                    }}
                  />
                )}
              </div>
            );
          })}

          {/* Link to another item — progressive disclosure, only when <= 1 channel */}
          {connectedChannelCount <= 1 && itemCode && (
            <>
              <Divider dividerMx={dividerMx} />
              <button
                type="button"
                className={cn(
                  "flex items-center gap-2 w-full text-left hover:bg-muted/50 transition-colors",
                  rowPx, "py-3",
                )}
                onClick={() => setShowLinkDialog(true)}
              >
                <Link2 className="h-4 w-4 text-primary/70" />
                <span className="text-sm text-primary">Link to another item&apos;s listings</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Sync Activity timeline (Plan 192) */}
      {itemCode && <SyncActivityTimeline itemCode={itemCode} />}

      {/* Link dialog */}
      {itemCode && (
        <LinkItemDialog
          open={showLinkDialog}
          onOpenChange={setShowLinkDialog}
          sourceItemCode={itemCode}
          onLinked={handleLinked}
        />
      )}

      {/* List on Channel dialog (Plan 192, Epic 06) */}
      {itemCode && listOnChannelState && (
        <ListOnChannelSheet
          open={!!listOnChannelState}
          onOpenChange={(open) => { if (!open) setListOnChannelState(null); }}
          channel={listOnChannelState.channel}
          connectionName={listOnChannelState.connectionName}
          itemCode={itemCode}
          itemName={itemName}
          price={itemPrice}
          stock={itemStock}
        />
      )}
    </div>
  );
}
